<template>
  <HomeSection
    v-if="places.length"
    :title="t('home.placesNearby')"
  >
    <RouterLink
      v-for="place in places"
      :key="place.id"
      :to="`/order/${place.id}`"
      class="place"
      draggable="false"
    >
      <img
        v-if="photoOf(place)"
        :src="photoOf(place)"
        class="place__img"
        alt=""
      >
      <div
        v-else
        class="place__img place__img--empty"
      >
        <IconBase
          :path="`category/${place.category_id}.svg`"
          class="place__icon"
        />
      </div>

      <div class="place__row">
        <span class="place__name">{{ place.title }}</span>
        <span
          v-if="Number(place.rating) > 0"
          class="place__rating"
        >
          <span class="place__star">★</span>{{ Number(place.rating).toFixed(1) }}
        </span>
      </div>
      <div class="place__category">
        {{ categoryName(place.category_id) }}
      </div>
    </RouterLink>
  </HomeSection>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { IconBase } from '@/UI'
import { getDistance } from '@/helpers'
import { useStore } from '@/states/index'
import { useMapStore } from '@/states/mapStore'
import { useMainEntities } from '@/states/mainEntities'
import { getPlaceById } from '../homeApi'
import HomeSection from './HomeSection.vue'

const PLACES_LIMIT = 6

const { t } = useI18n()
const store = useStore()
const mapStore = useMapStore()
const mainEntities = useMainEntities()

const places = ref([])
let loadedKey = ''

const nearestIds = computed(() => {
  const position = store.coordinates
  if (!position?.length || !mapStore.placemarks.length) {return []}

  return mapStore.placemarks
    .filter((el) => !el.offer)
    .map((el) => ({ id: el.id, distance: Number(getDistance([el.lat, el.lon], position)) || 0 }))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, PLACES_LIMIT)
    .map((el) => el.id)
})

watch(nearestIds, async (ids) => {
  const key = ids.join(',')
  if (!ids.length || key === loadedKey) {return}
  loadedKey = key

  const details = await Promise.all(ids.map(getPlaceById))
  places.value = details.filter(Boolean)
}, { immediate: true })

const photoOf = (place) => place.payload?.photos?.[0]?.path || place.logo || ''

const categoryName = (id) => (mainEntities.category || []).find((el) => el.id === id)?.name || ''
</script>

<style lang="scss" scoped>
.place {
  flex: none;
  width: 170px;
  color: inherit;
  text-decoration: none;

  &__img {
    display: block;
    width: 170px;
    height: 96px;
    border-radius: 18px;
    object-fit: cover;

    &--empty {
      background: #e9f1fb;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &__icon {
    width: 40px;
    height: 40px;
  }

  &__row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 6px;
    margin-top: 8px;
  }

  &__name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-family: "NunitoBold";
    color: #26384b;
  }

  &__rating {
    flex: none;
    font-size: 13px;
    font-family: "NunitoBold";
    color: #6b7090;
  }

  &__star {
    margin-right: 2px;
    color: #f5b301;
  }

  &__category {
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    font-family: "NunitoRegular";
    color: #8f98ab;
  }
}

[theme="dark"] {
  .place__name {
    color: #fff;
  }

  .place__img--empty {
    background: #37363c;
  }
}
</style>
